import { Component, DestroyRef, inject } from '@angular/core';
import { FlashActivatedHomeComponent } from 'src/app/admin/parcs/flash/flash-activated-home/flash-activated-home.component';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { AmanoOneComponent } from '../amano-one/amano-one.component';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { ParcsOrganizations } from 'src/app/shared/code/constants-enums/lanai-enums';
import { NgIf } from '@angular/common';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  IParcsGetLotConfigurationResponse,
  IParcsListResponse,
  IAmanoOneLotConfigurationSettings,
  IAmanoOneScheduleLprCredentialStatusUpdateRequest,
} from 'src/app/shared/code/interfaces/parcs-interfaces';
import { ProcessingBannerComponent } from 'src/app/shared/ui/processing-banner/processing-banner.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { DialogUpdateLprCredentialStatusComponent } from 'src/app/core/dialog/dialog-update-lpr-credential-status/dialog-update-lpr-credential-status.component';
import { AmanoOneCredentialSettingsComponent } from 'src/app/profiles/parker-v2/parker-parcs/amano-one-credential-settings/amano-one-credential-settings.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AmanoOneAdminMultipleFacilityMappingComponent } from '../amano-one/amano-one-admin-multiple-facility-mapping/amano-one-admin-multiple-facility-mapping.component';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { GenericConfirmationDialogComponent } from 'src/app/core/dialog/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { FlashSetupHomeComponent } from 'src/app/admin/parcs/flash/flash-setup-home/flash-setup-home.component';

@Component({
  selector: 'app-parcs-home',
  standalone: true,
  imports: [
    MatButtonToggleModule,
    MatTabsModule,
    MatListModule,
    AmanoOneComponent,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    ProcessingBannerComponent,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    AmanoOneCredentialSettingsComponent,
    MatCheckboxModule,
    AmanoOneAdminMultipleFacilityMappingComponent,
    MatSlideToggleModule,
    FlashSetupHomeComponent,
    FlashActivatedHomeComponent,
  ],
  templateUrl: './parcs-home.component.html',
  styleUrl: './parcs-home.component.scss',
  providers: [ParcsDataService],
})
export class ParcsHomeComponent {
  activationInProgress: boolean = false;
  isParcsInAddMode: boolean = true;
  selectedParcsName: string = 'TODO: Get Parcs Name';
  isParcsActivated: boolean = false;
  readonly parcOptions = Object.values(ParcsOrganizations);
  selectedParc: string = '';
  lprEnableStatus: string = 'lprEnabled';
  cardNumberPrefix: string = 'none_';
  parcsList: IParcsListResponse[] = [];
  selectedParcsId: string = '';
  lotParcsConfiguration: IParcsGetLotConfigurationResponse = {} as IParcsGetLotConfigurationResponse;
  deactivationInProgress: boolean = false;
  isLprEnabled: boolean = false;

  /// Amano One only. Empty for other PARCS vendors.
  facilityId: string = '';
  isLprUpdatingAccessHoldersInProgress: boolean = false;
  multipleFacilityMappingEnabled: boolean = false;

  //Services
  _appGeneralState = inject(AppGeneralStateService);
  _messageDialogService = inject(MessageDialogService);
  _formBuilder = inject(FormBuilder);
  _parcsDataService = inject(ParcsDataService);
  _destroyRefService = inject(DestroyRef);
  _dialog = inject(MatDialog);

  readonly parcsSelectionForm = this._formBuilder.group({
    parcName: new FormControl<string | null>(null, Validators.required),
  });

  constructor() {}

  ngOnInit() {
    this.getParcsOrganizationList();
    this.getParcsLotConfigurationData(this._appGeneralState.currentLotNumber);
  }

  onSelectionChange(event: MatSelectionListChange) {
    const selectedValues = event.source.selectedOptions.selected.map((option) => option.value);
    this.selectedParc = selectedValues.join(', ');
    this.selectedParcsName = this.selectedParc.toUpperCase();
  }

  onSaveCardNumberPrefix(): void {
    this._parcsDataService
      .updateAmanoOneCardNumberPrefixForLot(this._appGeneralState.currentLotNumber, this.cardNumberPrefix)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this._messageDialogService.snackBarTopCenterTimed(
            `The Card Number Prefix has been successfully updated.`,
            3000,
          );
        },
        error: (err: any) => {
          this._messageDialogService.snackBarTopCenterTimed(
            `Error updating Card Number Prefix.  Please try again.`,
            3000,
          );
        },
      });
  }

  onActivateParcs() {
    this.activationInProgress = true;
  }

  onCancelActivateParcs() {
    this.activationInProgress = false;
    this.selectedParcsName = '';
  }

  onDeactivateParcs() {
    this.deleteAmanoOneLotConfiguration(this._appGeneralState.currentLotNumber);
  }

  onLprStatusChanged(): void {
    const lprEnabledStatus = this.lprEnableStatus === 'lprEnabled';

    // Show dialog to ask user when to update existing parkers
    this.displayLprStatusChangeDialog();

    // This checks to see if the lot setup has been finished.
    // If the lot setup has been completed, a LPREnabled change requires updating all Parkers with the new status
    if (this.lotParcsConfiguration.LotInitalizationCompleted) {
    } else {
      // Lot setup not completed, just update the LPR status directly
      // this.updateParcsLprEnabledStatusForLot(this._appGeneralState.currentLotNumber, lprEnabledStatus);
    }
  }

  displayLprStatusChangeDialog(): void {
    var status: boolean = false;

    const dialogRef = this._dialog.open(DialogUpdateLprCredentialStatusComponent, {
      width: '600px',
      height: '400px',
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === 'now') {
        this.isLprUpdatingAccessHoldersInProgress = true;
        // Update LPR status and immediately update all existing parkers
        const lprEnabledStatus = this.lprEnableStatus === 'lprEnabled';
        status = await this.updateParcsLprEnabledStatusForLot(this._appGeneralState.currentLotNumber, lprEnabledStatus);
        if (status) {
          this._messageDialogService.snackBarTopCenterTimed(
            'the LPR enabled status has been updated in spACE-AR.  Stand by while we updating Amano One Access Holders with the new setting.',
            4000,
          );

          if (
            await this.updateLprEnabledStatusForAllParkers(
              lprEnabledStatus,
              this._appGeneralState.currentLotNumber,
              this._appGeneralState.currentUserEmailAddress ?? '',
            )
          ) {
            this.isLprUpdatingAccessHoldersInProgress = false;
            this._messageDialogService.snackBarTopCenterTimed(
              'The LPR Access Holder updates have been completed.  Check your email for details of the update.',
              4000,
            );
          } else {
            this.isLprUpdatingAccessHoldersInProgress = false;
            this._messageDialogService.snackBarTopCenterTimed(
              'The LPR Access Holder updates failed.  Check your email for details of the update.',
              4000,
            );
          }
        }
      } else if (result === 'schedule') {
        this.isLprUpdatingAccessHoldersInProgress = true;
        // Update LPR status but schedule parker updates for later
        const lprEnabledStatus = this.lprEnableStatus === 'lprEnabled';
        status = await this.scheduleLprEnabledStatusForAllParkers(
          lprEnabledStatus,
          this._appGeneralState.currentLotNumber,
          this._appGeneralState.currentUserEmailAddress ?? '',
        );
        if (status) {
          this._messageDialogService.snackBarTopCenterTimed(
            'the LPR enabled status has been updated in spACE-AR, and the Amano One Access Holder update has been scheduled to run later today.',
            4000,
          );
          this.isLprUpdatingAccessHoldersInProgress = false;
        } else {
          this.isLprUpdatingAccessHoldersInProgress = false;
          this._messageDialogService.snackBarTopCenterTimed(
            'The LPR Access Holder update scheduling failed. Please try again later.',
            4000,
          );
        }
      } else {
        // User cancelled - revert the toggle back to previous state
        this.lprEnableStatus = this.lprEnableStatus === 'lprEnabled' ? 'lprDisabled' : 'lprEnabled';
      }
    });
  }

  onSelectParcsName(): void {
    const parcName = this.parcsSelectionForm.get('parcName')?.value ?? '';
    this.selectedParcsId = parcName.toUpperCase();
    this.selectedParcsName =
      this.parcsList.find((parc) => parc.parcsId.toUpperCase() === this.selectedParcsId.toUpperCase())
        ?.parcsDisplayName || '';
  }

  // getParkcsListFromEnum(): string[] {
  //   return Object.values(ParcsOrganizations);
  // }

  getParcsOrganizationList() {
    this._parcsDataService
      .getParcsOrganizationList()
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.parcsList = res;
        },
        error: (err: any) => {},
        complete: () => {},
      });
  }

  getParcsLotConfigurationData(lotNumber: string) {
    this._parcsDataService
      .getParcsLotConfigurationByLotNumber(lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.lotParcsConfiguration = res;
          this.selectedParcsName = this.lotParcsConfiguration.parcsDisplayName;
          if (this.lotParcsConfiguration.lotNumber == lotNumber) {
            this.isParcsInAddMode = false;
          } else this.isParcsInAddMode = true;
        },
        error: (err: any) => {
          this.isParcsInAddMode = true; // no configuration found, so we are in add mode
        },
        complete: () => {
          const amanoSettings = JSON.parse(this.lotParcsConfiguration.settings.toLowerCase());
          if (amanoSettings.lprenabled) {
            this.isLprEnabled = true;
            this.lprEnableStatus = 'lprEnabled';
          } else {
            this.isLprEnabled = false;
            this.lprEnableStatus = 'lprDisabled';
          }

          // Set the card number prefix with fallback (try both case variations)
          this.cardNumberPrefix = amanoSettings.cardnumberprefix || '';

          // The settings JSON is lowercased before parsing, so every key is lowercase - facilityId
          // would be undefined here.
          this.facilityId = amanoSettings.facilityid || '';
        },
      });
  }

  async updateParcsLprEnabledStatusForLot(lotNumber: string, lprEnabledStatus: boolean): Promise<boolean> {
    let status = false;
    try {
      status =
        (await this._parcsDataService
          .updateAmanoOneParcsLprEnabledStatusForLot(lotNumber, lprEnabledStatus)
          .pipe(takeUntilDestroyed(this._destroyRefService))
          .toPromise()) ?? false;
      if (status) {
        if (this.lprEnableStatus == 'lprEnabled') {
          this.isLprEnabled = true;
        } else {
          this.isLprEnabled = false;
        }
      }
    } catch (err: any) {
      this.isLprUpdatingAccessHoldersInProgress = false;
      this._messageDialogService.snackBarTopCenterTimed(`Error updating LPR Enabled Status.  Please try again.`, 3000);

      // Reset status back to previous value since update failed.
      this.lprEnableStatus === 'lprEnabled'
        ? (this.lprEnableStatus = 'lprDisabled')
        : (this.lprEnableStatus = 'lprEnabled');
    }
    return status;
  }

  deleteAmanoOneLotConfiguration(lotNumber: string) {
    this.deactivationInProgress = true;

    this._parcsDataService
      .updateParcsLotSettingsToDeletedStatusByLotNumber(lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this._messageDialogService.snackBarTopCenterTimed(
            `Lot {lotNumber}'s configuration has been successfully deleted.`,
            3000,
          );
        },
        error: (err: any) => {
          this._messageDialogService.snackBarTopCenterTimed(
            `Error deleting this configuration.  Please try again.`,
            3000,
          );
          this.deactivationInProgress = false;
        },
        complete: () => {
          this.isParcsActivated = false;
          this.lprEnableStatus = 'lprDisabled';
          this.activationInProgress = false;
          this.selectedParcsName = '';
          this.lotParcsConfiguration = {} as IParcsGetLotConfigurationResponse;
          this.isParcsInAddMode = true;
          this.deactivationInProgress = false;
        },
      });
  }

  async updateLprEnabledStatusForAllParkers(
    credentialSetting: boolean,
    lotNumber: string,
    notificationEmailAddress: string,
  ): Promise<boolean> {
    try {
      const status = await this._parcsDataService
        .UpdateLprAsPrimaryChange(credentialSetting, lotNumber, notificationEmailAddress)
        .pipe(takeUntilDestroyed(this._destroyRefService))
        .toPromise();

      return status || false;
    } catch (err: any) {
      return false;
    }
  }

  async scheduleLprEnabledStatusForAllParkers(
    credentialSetting: boolean,
    lotNumber: string,
    notificationEmailAddress: string,
  ): Promise<boolean> {
    try {
      const lprData: IAmanoOneScheduleLprCredentialStatusUpdateRequest = {
        lotNumber: lotNumber,
        licenseAsCredential: credentialSetting,
        notificationEmailAddress: notificationEmailAddress,
      };

      const status = await this._parcsDataService
        .ScheduleLprAsPrimaryChange(lprData)
        .pipe(takeUntilDestroyed(this._destroyRefService))
        .toPromise();

      return status || false;
    } catch (err: any) {
      return false;
    }
  }

  onMultipleFacilityMappingToggleChanged(event: MatSlideToggleChange): void {
    const previousState = this.multipleFacilityMappingEnabled;

    const dialogRef = this._dialog.open(GenericConfirmationDialogComponent, {
      width: '400px',
      height: '300px',
      data: {
        title: 'Change Lot Multi-Facility Feature',
        line1: 'Click Yes to change the Lot Multi-Facility Feature setting.',
        line2: '',
        button: 'Yes',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.multipleFacilityMappingEnabled = event.checked;
        this.updateMultipleFacilityMappingStatus();
      } else {
        this.multipleFacilityMappingEnabled = previousState;
        event.source.checked = previousState;
      }
    });
  }

  updateMultipleFacilityMappingStatus(): void {}
}
